/**
 * App Share Request Page — 라이브러리 공유 신청 (단일 화면)
 *
 * 산출물 선택 → 앱 정보 → 공개 범위 → 제출을 한 화면에서 처리
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Button, Select, useToast } from '../components/ui';
import './AppShareRequestPage.css';

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface Artifact {
  id: string;
  fileName: string;
  size: string;
  bot: string;
  chatDate: string;
  type: 'HTML 문서' | '웹앱' | 'HTML 대시보드';
}

// ── 목업 산출물 목록 ──────────────────────────────────────────────────────────

const ARTIFACTS: Artifact[] = [
  { id: 'art1',  fileName: 'aws_cost_anomaly_card.html',     size: '36KB',  bot: '워킹온 DB 커넥터',    chatDate: '10.23(금) 14:02', type: 'HTML 문서' },
  { id: 'art2',  fileName: 'ec2_rightsizing_calc.html',      size: '28KB',  bot: '웹검색',             chatDate: '10.22(목) 10:15', type: '웹앱' },
  { id: 'art3',  fileName: 'partner_revenue_q3.html',        size: '42KB',  bot: '워킹온 DB 커넥터',    chatDate: '10.21(수) 16:30', type: 'HTML 대시보드' },
  { id: 'art4',  fileName: 'meeting_action_items.html',      size: '18KB',  bot: '기본 채팅',          chatDate: '10.21(수) 09:00', type: '웹앱' },
  { id: 'art5',  fileName: 'support_ticket_form.html',       size: '22KB',  bot: 'Support-on 가이드', chatDate: '10.20(화) 14:22', type: '웹앱' },
  { id: 'art6',  fileName: 'weekly_it_news_brief.html',      size: '31KB',  bot: '웹크롤링 워크플로우', chatDate: '10.20(화) 08:00', type: 'HTML 문서' },
];

const CATEGORIES = ['데이터·리포트', '클라우드', '영업지원', '기술지원', '업무생산성', '개발'];

const SCOPE_OPTIONS = [
  { id: 'tenant', label: '에티버스 테넌트 전체',  hint: '내 테넌트 안 모든 사용자' },
  { id: 'org',    label: '특정 고객사',           hint: '선택한 고객사 사용자만' },
  { id: 'people', label: '지정 사용자',           hint: '이름·이메일로 직접 선택' },
  { id: 'me',     label: '나만 보기',             hint: '관리자 승인 없이 저장' },
];

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function AppShareRequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // 내 산출물에서 전달된 사전 선택 산출물
  const preArt = (location.state as any)?.artifact as Artifact | undefined;

  const [selected, setSelected] = useState<Artifact | null>(preArt ?? null);
  const [appName,  setAppName]  = useState(() => {
    if (!preArt) return '';
    const base = preArt.fileName.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
    return base.charAt(0).toUpperCase() + base.slice(1);
  });
  const [shortDesc, setShortDesc] = useState('');
  const [category,  setCategory]  = useState(CATEGORIES[0]);
  const [kind,      setKind]      = useState<'HTML 문서' | '웹앱'>(() =>
    preArt?.type === '웹앱' ? '웹앱' : 'HTML 문서'
  );
  const [howto, setHowto] = useState('');
  const [scope, setScope] = useState('에티버스 테넌트 전체');

  const handleSelect = (art: Artifact) => {
    setSelected(art);
    if (!appName) {
      const base = art.fileName.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
      setAppName(base.charAt(0).toUpperCase() + base.slice(1));
      setKind(art.type === '웹앱' ? '웹앱' : 'HTML 문서');
    }
  };

  const canSubmit = !!selected && appName.trim().length > 0 && shortDesc.trim().length > 0;

  const handleSaveDraft = () => {
    showToast('success', '임시 저장됐습니다.');
    navigate('/library/my');
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    showToast('success', `"${appName}" 공유 신청이 완료됐습니다. 심사 결과를 기다려주세요.`);
    navigate('/library/my');
  };

  return (
    <>
      <Topbar
        title="라이브러리 공유 신청"
        subtitle="챗봇 대화에서 만든 산출물을 라이브러리에 올립니다."
        leading={
          <button type="button" className="asr-back" onClick={() => navigate('/library/my')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
        }
      />

      <div className="page-content">
        <div className="page-content-inner asr-container">

          {/* 브레드크럼 */}
          <nav className="asr-breadcrumb">
            <Link to="/library">라이브러리</Link><span>/</span>
            <Link to="/library/my">내가 공유한 항목</Link><span>/</span>
            <span>공유 신청</span>
          </nav>

          <div className="asr-layout">
            {/* ── 메인 폼 ── */}
            <div className="asr-form-area">

              {/* 섹션 1: 산출물 선택 */}
              <section className="asr-card">
                <h2 className="asr-card-title">
                  <span className="asr-section-num">1</span>산출물 선택
                  {selected && <span className="asr-section-check">✓</span>}
                </h2>
                {selected ? (
                  /* 선택 완료 상태 */
                  <div className="asr-selected-row">
                    <div className="asr-artifact-icon-sm">HTML</div>
                    <div className="asr-artifact-info">
                      <strong>{selected.fileName}</strong>
                      <span>{selected.bot} 챗봇 · {selected.chatDate} · {selected.size}</span>
                    </div>
                    {!preArt && (
                      <button type="button" className="asr-change-btn" onClick={() => setSelected(null)}>
                        변경
                      </button>
                    )}
                  </div>
                ) : (
                  /* 산출물 목록 */
                  <ul className="asr-artifact-list">
                    {ARTIFACTS.map((art) => (
                      <li key={art.id}>
                        <button
                          type="button"
                          className="asr-artifact-item"
                          onClick={() => handleSelect(art)}
                        >
                          <span className="asr-artifact-icon">HTML</span>
                          <div className="asr-artifact-info">
                            <strong>{art.fileName}</strong>
                            <span>{art.bot} 챗봇 · {art.chatDate} · {art.size}</span>
                          </div>
                          <span className="asr-artifact-type">{art.type}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* 섹션 2: 앱 정보 */}
              <section className="asr-card">
                <h2 className="asr-card-title">
                  <span className="asr-section-num">2</span>앱 정보
                </h2>
                <div className="asr-form">
                  <label className="asr-label">
                    <span>앱 이름 <span className="asr-required">*</span></span>
                    <input
                      type="text"
                      className="asr-input"
                      value={appName}
                      onChange={(e) => setAppName(e.target.value)}
                      placeholder="앱 이름을 입력하세요"
                      maxLength={50}
                    />
                  </label>
                  <label className="asr-label">
                    <span>한 줄 설명 <span className="asr-required">*</span></span>
                    <input
                      type="text"
                      className="asr-input"
                      value={shortDesc}
                      onChange={(e) => setShortDesc(e.target.value)}
                      placeholder="이 앱이 무엇을 하는지 한 문장으로 설명하세요"
                      maxLength={100}
                    />
                  </label>
                  <div className="asr-form-row">
                    <label className="asr-label">
                      카테고리
                      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </Select>
                    </label>
                    <fieldset className="asr-fieldset">
                      <legend>유형</legend>
                      <div className="asr-radio-group">
                        {(['HTML 문서', '웹앱'] as const).map((k) => (
                          <label key={k} className={`asr-radio-label${kind === k ? ' asr-radio-label--checked' : ''}`}>
                            <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
                            {k}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                  <label className="asr-label">
                    <span>사용 방법 <span className="asr-optional">(선택)</span></span>
                    <textarea
                      className="asr-textarea"
                      rows={3}
                      value={howto}
                      onChange={(e) => setHowto(e.target.value)}
                      placeholder="앱을 어떻게 사용하는지 안내를 입력하세요"
                    />
                  </label>
                </div>
              </section>

              {/* 섹션 3: 공개 범위 */}
              <section className="asr-card">
                <h2 className="asr-card-title">
                  <span className="asr-section-num">3</span>공개 범위 · 권한
                </h2>
                <div className="asr-notice">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>
                  </svg>
                  <span>앱은 <strong>내 테넌트(에티버스) 안에서만</strong> 공유할 수 있어요.</span>
                </div>
                <div className="asr-scope-grid">
                  {SCOPE_OPTIONS.map((opt) => {
                    const checked = scope === opt.label;
                    return (
                      <label key={opt.id} className={`asr-scope-option${checked ? ' asr-scope-option--checked' : ''}`}>
                        <input type="radio" name="scope" checked={checked} onChange={() => setScope(opt.label)} />
                        <span>
                          <strong>{opt.label}</strong>
                          <span>{opt.hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {scope === '나만 보기' && (
                  <div className="asr-scope-note">나만 보기는 관리자 승인 없이 즉시 저장됩니다.</div>
                )}
              </section>

            </div>

            {/* ── 오른쪽 사이드 패널 ── */}
            <aside className="asr-side">
              <div className="asr-side-panel">
                <Button
                  className="asr-submit-full-btn"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  title={!canSubmit ? '산출물 선택, 앱 이름, 설명을 입력해주세요' : undefined}
                >
                  검토 요청하기
                </Button>
                <button type="button" className="asr-btn-ghost" onClick={handleSaveDraft}>
                  임시 저장
                </button>
                {!canSubmit && (
                  <ul className="asr-required-list">
                    {!selected    && <li>산출물을 선택해주세요</li>}
                    {!appName     && <li>앱 이름을 입력해주세요</li>}
                    {!shortDesc   && <li>한 줄 설명을 입력해주세요</li>}
                  </ul>
                )}
              </div>

              <div className="asr-side-panel">
                <h3 className="asr-side-title">공유 전 안내</h3>
                <ul className="asr-info-list">
                  <li>산출물 파일만 올라가고, 대화 내용은 공유되지 않아요.</li>
                  <li>자동 보안 점검(외부 호출·개인정보·하드코딩) 후 관리자 승인을 거쳐요.</li>
                  <li>공개 범위에 따라 승인 단계가 달라집니다.</li>
                </ul>
              </div>
            </aside>
          </div>

        </div>
      </div>
    </>
  );
}

export default AppShareRequestPage;
